import type { LucideIcon } from "lucide-react";
import type { ButtonHTMLAttributes, Ref } from "react";
import { cn } from "@/shared/lib/utils";
import { Spinner } from "./spinner";
import { Tooltip } from "./tooltip";
import type { Placement } from "./floating";

export type IconButtonVariant = "ghost" | "outline" | "subtle" | "primary" | "danger" | "danger-ghost";
export type IconButtonSize = "xs" | "sm" | "md" | "lg";

const variants: Record<IconButtonVariant, string> = {
  ghost: "text-fg-muted hover:bg-surface-hover hover:text-fg",
  outline: "border border-border-strong bg-surface text-fg-muted shadow-xs hover:bg-surface-hover hover:text-fg",
  subtle: "bg-surface-sunken text-fg-muted hover:bg-surface-hover hover:text-fg",
  primary: "bg-primary text-primary-fg hover:bg-primary-hover",
  danger: "bg-danger text-on-danger hover:bg-danger-hover",
  "danger-ghost": "text-danger-fg hover:bg-danger-subtle",
};

const sizes: Record<IconButtonSize, { box: string; icon: string }> = {
  xs: { box: "h-6 w-6 rounded-sm", icon: "h-3.5 w-3.5" },
  sm: { box: "h-8 w-8 rounded-md", icon: "h-4 w-4" },
  md: { box: "h-9 w-9 rounded-md", icon: "h-4 w-4" },
  lg: { box: "h-10 w-10 rounded-md", icon: "h-5 w-5" },
};

export type IconButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  icon: LucideIcon;
  /** Obrigatório: vira aria-label e o texto do tooltip. */
  label: string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  loading?: boolean;
  /** false desliga o tooltip (ex.: quando o rótulo já está visível ao lado). */
  tooltip?: boolean;
  tooltipSide?: Placement;
  /** Estado "ligado" de botões alternáveis (mudo, espera…): aria-pressed + realce. */
  pressed?: boolean;
  ref?: Ref<HTMLButtonElement>;
};

/** Botão só com ícone. Sempre acessível: aria-label + tooltip com o mesmo texto. */
export function IconButton({
  icon: Icon,
  label,
  variant = "ghost",
  size = "md",
  loading,
  tooltip = true,
  tooltipSide = "top",
  pressed,
  className,
  disabled,
  type = "button",
  ...props
}: IconButtonProps) {
  const s = sizes[size];
  const btn = (
    <button
      type={type}
      aria-label={label}
      aria-pressed={pressed}
      disabled={disabled || loading}
      className={cn(
        "inline-flex shrink-0 items-center justify-center transition-colors duration-150",
        "disabled:pointer-events-none disabled:opacity-45",
        s.box,
        variants[variant],
        pressed && "bg-brand-subtle text-brand-accent hover:bg-brand-subtle",
        className,
      )}
      {...props}
    >
      {loading ? <Spinner className={s.icon} /> : <Icon className={s.icon} strokeWidth={2} aria-hidden />}
    </button>
  );
  return tooltip ? (
    <Tooltip content={label} side={tooltipSide}>
      {btn}
    </Tooltip>
  ) : (
    btn
  );
}
